import { useRef } from 'react';

const delay = (n) => new Promise((resolve) => setTimeout(resolve, n));

const cancellablePromise = (promise) => {
    let isCanceled = false;

    const wrappedPromise = new Promise((resolve, reject) => {
        promise.then(
            (value) => (isCanceled ? reject({ isCanceled, value }) : resolve(value)),
            (error) => reject({ isCanceled, error })
        );
    });

    return {
        promise: wrappedPromise,
        cancel: () => (isCanceled = true)
    };
};

const useCancellablePromises = () => {
    const pendingPromises = useRef([]);

    const appendPendingPromise = (promise) =>
        (pendingPromises.current = [...pendingPromises.current, promise]);

    const removePendingPromise = (promise) =>
        (pendingPromises.current = pendingPromises.current.filter((p) => p !== promise));

    const clearPendingPromises = () => pendingPromises.current.map((p) => p.cancel());

    return {
        appendPendingPromise,
        removePendingPromise,
        clearPendingPromises
    };
};

export const useClickPreventionOnDoubleClick = ({
    onClick,
    onDoubleClick,
    config = { delay: 200 }
}) => {
    const { appendPendingPromise, removePendingPromise, clearPendingPromises } =
        useCancellablePromises();

    const handleClick = () => {
        clearPendingPromises();
        const waitForClick = cancellablePromise(delay(config.delay));
        appendPendingPromise(waitForClick);

        return waitForClick.promise
            .then(() => {
                removePendingPromise(waitForClick);
                onClick();
            })
            .catch((errorInfo) => {
                removePendingPromise(waitForClick);
                if (!errorInfo.isCanceled) {
                    throw errorInfo.error;
                }
            });
    };

    const handleDoubleClick = () => {
        clearPendingPromises();
        onDoubleClick();
    };

    return { handleClick, handleDoubleClick };
};
